import {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {originalPath} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";

/**
 * The approver's edits on their copy of the tree (`approve/taxonomy/{id}/state/`): reverting one change and
 * a note per category. The rules are the current approval page's (`TaxonomyApprovalDelegate`), on plain
 * trees: each edit returns a new tree, the input is never changed. Unlike the builder's tree, the copy
 * keeps its `changes`: a reverted change stays in it, marked `reverted`.
 */

type Entry = {node: m_taxonomy.Tree, parent: m_taxonomy.Tree | null};

function clone(node: m_taxonomy.Tree): m_taxonomy.Tree {
    return {
        ...node,
        values: {...node.values, changes: node.values.changes?.map(c => ({...c})), authors: node.values.authors && [...node.values.authors]},
        sources: [...node.sources],
        children: node.children.map(clone),
    };
}

function indexAll(root: m_taxonomy.Tree): Map<number, Entry> {
    const map = new Map<number, Entry>();
    const visit = (node: m_taxonomy.Tree, parent: m_taxonomy.Tree | null) => {
        map.set(node.id, {node, parent});
        node.children.forEach(c => visit(c, node));
    };
    visit(root, null);
    return map;
}

/** Labels from L1 down to the category itself */
function pathOf(all: Map<number, Entry>, id: number): string[] {
    const labels: string[] = [];
    for (let e = all.get(id); e?.parent; e = all.get(e.parent.id)) labels.unshift(e.node.label);
    return labels;
}

/** Every category at a path of labels from L1, deleted ones included (the root for an empty path). */
function findAll(root: m_taxonomy.Tree, path: string[]): m_taxonomy.Tree[] {
    let level = [root];
    for (const label of path) level = level.flatMap(n => n.children.filter(c => c.label === label));
    return [...new Map(level.map(n => [n.id, n])).values()];
}

function descendantsOf(node: m_taxonomy.Tree): m_taxonomy.Tree[] {
    return [node, ...node.children.flatMap(descendantsOf)];
}

const openChanges = (node: m_taxonomy.Tree) => node.values.changes?.filter(c => !c.reverted);

/** Every category in the branch has only open changes of this type (an addition / deletion of the whole branch). */
function isHomogeneous(node: m_taxonomy.Tree, type: m_taxonomy.ChangeType): boolean {
    return descendantsOf(node).every(n => openChanges(n)?.every(c => c.type === type) ?? false);
}

/** Where a move is undone to: the old parent's path, with the category's current name. */
function reverseMoveTarget(root: m_taxonomy.Tree, node: m_taxonomy.Tree, change: {original_location: string[]}): string[] {
    return [...originalPath(root, change).slice(0, -1), node.label];
}

/** The change itself in the tree: `changes` keeps its objects, so the row's change is found by identity. */
function changeIndex(node: m_taxonomy.Tree, change: m_taxonomy.Change): number {
    return node.values.changes?.indexOf(change) ?? -1;
}

/**
 * Whether one change can be put back: nothing may take its old place (a sibling with the old name, a
 * category back at the old path), a move needs its old parent once, and a deletion / addition can only be
 * undone for its whole branch at once.
 */
export function canRevert(root: m_taxonomy.Tree, nodeId: number, change: m_taxonomy.Change): boolean {
    const all = indexAll(root);
    const node = all.get(nodeId)?.node;
    if (!node || change.reverted) return false;
    switch (change.type) {
        case 'removed':
            return findAll(root, pathOf(all, nodeId)).every(n => n.id === nodeId) && isHomogeneous(node, 'removed');
        case 'renamed':
            return findAll(root, [...pathOf(all, nodeId).slice(0, -1), change.original_label]).length === 0;
        case 'moved': {
            const target = reverseMoveTarget(root, node, change);
            return findAll(root, target).length === 0 && findAll(root, target.slice(0, -1)).length === 1;
        }
        case 'added':
            return isHomogeneous(node, 'added');
        case 'updated':
            return true;
    }
}

/** The operation saved for a revert, as the current approval page names it. */
export function revertOperation(change: m_taxonomy.Change): m_taxonomy.Operation {
    switch (change.type) {
        case 'removed': return m_taxonomy.Operation.Add;
        case 'renamed': return m_taxonomy.Operation.Update;
        case 'moved': return m_taxonomy.Operation.Move;
        case 'added': return m_taxonomy.Operation.Delete;
        case 'updated': return m_taxonomy.Operation.Update;
    }
}

function touch(node: m_taxonomy.Tree, authorId: number) {
    node.values.authors = [...(node.values.authors ?? [])];
    m_taxonomy.addAuthor(node.values, authorId);
    node.values.approval_touched = true;
}

/**
 * Puts one change back (check `canRevert` first) and marks it `reverted`; the category is marked as
 * touched by the approver. A deletion / addition is undone for the whole branch.
 */
export function revertChange(root: m_taxonomy.Tree, nodeId: number, change: m_taxonomy.Change, authorId: number): m_taxonomy.Tree {
    const index = indexAll(root).get(nodeId);
    const at = index ? changeIndex(index.node, change) : -1;
    const next = clone(root);
    const all = indexAll(next);
    const entry = all.get(nodeId);
    const own = entry?.node.values.changes?.[at];
    if (!entry || !own) return root;
    const node = entry.node;
    own.reverted = true;

    switch (change.type) {
        case 'removed': {
            descendantsOf(node).forEach(n => {
                n.removed = false;
                n.values.changes?.forEach(c => c.reverted = true);
                touch(n, authorId);
            });
            // A category can't be back under a deleted one, so its deleted ancestors come back too: their
            // deletion is reverted with it (only theirs, their other deleted children stay deleted).
            for (let p = entry.parent; p; p = all.get(p.id)?.parent ?? null) {
                if (!p.removed) continue;
                p.removed = false;
                p.values.changes?.forEach(c => {
                    if (c.type === 'removed') c.reverted = true;
                });
                touch(p, authorId);
            }
            break;
        }
        case 'renamed':
            node.label = change.original_label;
            touch(node, authorId);
            break;
        case 'moved': {
            const target = findAll(next, reverseMoveTarget(next, node, change).slice(0, -1))[0];
            if (!target || !entry.parent) return root;
            entry.parent.children = entry.parent.children.filter(c => c.id !== nodeId);
            target.children = [...target.children, node];
            touch(node, authorId);
            break;
        }
        case 'added':
            descendantsOf(node).forEach(n => {
                n.removed = true;
                n.values.changes?.forEach(c => c.reverted = true);
                touch(n, authorId);
            });
            break;
        case 'updated':
            (node.values as Record<string, unknown>)[change.field] = change.original_value;
            touch(node, authorId);
            break;
    }
    return next;
}

/** The approver's note on one category (`approval_feedback_note`), shown to the requester. */
export function setApproverNote(root: m_taxonomy.Tree, nodeId: number, note: string): m_taxonomy.Tree {
    const map = (n: m_taxonomy.Tree): m_taxonomy.Tree => n.id === nodeId
        ? {...n, values: {...n.values, approval_feedback_note: note}, children: n.children}
        : {...n, children: n.children.map(map)};
    return map(root);
}
